import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import "./Template_1.css";
import Template_1_Header from "./Template_1_Header";
import Template_1_EnquiryPopup from "./Template_1_EnquiryPopup";
import Template_1_Footer from "./Template_1_Footer";
import Template_1_TopHeader from "./Template_1_TopHeader";
const Template_1_PackageDetails = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
  const [imageGallery, setImageGallery] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const getPackageDetails = async () => {
      if (!packageId) return;
      setLoading(true);
      try {
        const response = await apiService.get(
          `${config.GETPACKAGEDETAILS}/${packageId}`
        );
        setDetails(response);

        // Create image gallery from available images
        const gallery = [response.package.imageSrc];
        if (response.hotelBookings) {
          response.hotelBookings.forEach((hotel) => {
            if (hotel.imageUrl) gallery.push(hotel.imageUrl);
          });
        }
        if (response.sightSeeings) {
          response.sightSeeings.forEach((sight) => {
            if (sight.imageUrl) gallery.push(sight.imageUrl);
          });
        }
        setImageGallery(gallery);
      } catch (err) {
        console.error("Failed to fetch package details:", err);
        setError("Could not load package details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    getPackageDetails();
  }, [packageId]);

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === imageGallery.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? imageGallery.length - 1 : prevIndex - 1
    );
  };

  if (loading)
    return (
      <div className="Template_1-loading-screen">
        <div className="Template_1-loader">
          <div className="Template_1-plane-icon">✈️</div>
          <p>Preparing your dream vacation...</p>
        </div>
      </div>
    );
  if (error) return <div className="Template_1-error">{error}</div>;
  if (!details)
    return <div className="Template_1-error">Package not found</div>;

  // Extract data from the API response
  const pkg = details.package || {};
  const itineraries = details.itineraries || [];
  const includes = details.includes || [];
  const excludes = details.excludes || [];
  const flightBookings = details.flightBookings || [];
  const hotelBookings = details.hotelBookings || [];
  const termsandConditions = details.termsandConditions || [];
  const transfers = details.transfers || [];
  const sightSeeings = details.sightSeeings || [];

  // Fix for object rendering issue - safely extract destination name
  const getDestinationName = () => {
    if (!pkg.destination) return "Unknown Destination";

    // If destination is a string, return it directly
    if (typeof pkg.destination === "string") return pkg.destination;

    // If destination is an object with destinationName property
    if (pkg.destination.destinationName) return pkg.destination.destinationName;

    // If destination is an object but doesn't have destinationName
    return "Unknown Destination";
  };

  const destinationName = getDestinationName();

  return (
    <div className="Template_1 package-detail Template_1 active">
      <Template_1_TopHeader />
      <Template_1_Header />

      {/* Hero Section with Full-screen Image Slider */}
      <div className="Template_1-hero">
        <div
          className="Template_1-hero-image"
          style={{ backgroundImage: `url(${imageGallery[currentImageIndex]})` }}
        >
          <div className="Template_1-hero-overlay">
            <div className="Template_1-hero-content">
              <div className="Template_1-hero-badges">
                <span className="Template_1-badge bestseller">Bestseller</span>
              </div>
              <h1 className="Template_1-hero-title">{pkg.packageName}</h1>
              <div className="Template_1-hero-details">
                <div className="Template_1-detail-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{destinationName}</span>
                </div>
                <div className="Template_1-detail-item">
                  <i className="fas fa-clock"></i>
                  <span>{pkg.duration}</span>
                </div>
                <div className="Template_1-detail-item">
                  <i className="fas fa-user-friends"></i>
                  <span>{pkg.noOfPersons} People</span>
                </div>
              </div>
              <div className="Template_1-price-container">
                <div className="Template_1-price">
                  <span className="Template_1-price-from">from</span>
                  <span className="Template_1-price-amount">
                    ₹{pkg.price?.toLocaleString("en-IN")}
                  </span>
                  <span className="Template_1-price-person">per person</span>
                </div>
              </div>
              <button
                className="Template_1-cta-button"
                onClick={() => setShowEnquiryPopup(true)}
              >
                <i className="fas fa-envelope"></i>
                Enquire Now
              </button>
            </div>
          </div>
        </div>

        {/* Image Gallery Navigation */}
        {imageGallery.length > 1 && (
          <div className="Template_1-gallery-nav">
            <button className="Template_1-nav-btn" onClick={prevImage}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <div className="Template_1-gallery-dots">
              {imageGallery.map((_, index) => (
                <button
                  key={index}
                  className={`Template_1-dot ${
                    index === currentImageIndex ? "active" : ""
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
            <button className="Template_1-nav-btn" onClick={nextImage}>
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      {/* Floating Navigation */}
      <div className="Template_1-floating-nav">
        <div className="Template_1-nav-container">
          <button
            className={`Template_1-nav-item ${
              activeSection === "overview" ? "active" : ""
            }`}
            onClick={() => setActiveSection("overview")}
          >
            <i className="fas fa-binoculars"></i>
            Overview
          </button>
          <button
            className={`Template_1-nav-item ${
              activeSection === "itinerary" ? "active" : ""
            }`}
            onClick={() => setActiveSection("itinerary")}
          >
            <i className="fas fa-route"></i>
            Itinerary
          </button>
          <button
            className={`Template_1-nav-item ${
              activeSection === "accommodation" ? "active" : ""
            }`}
            onClick={() => setActiveSection("accommodation")}
          >
            <i className="fas fa-hotel"></i>
            Stay
          </button>
          <button
            className={`Template_1-nav-item ${
              activeSection === "inclusions" ? "active" : ""
            }`}
            onClick={() => setActiveSection("inclusions")}
          >
            <i className="fas fa-check-circle"></i>
            Inclusions
          </button>
          <button
            className={`Template_1-nav-item ${
              activeSection === "gallery" ? "active" : ""
            }`}
            onClick={() => setActiveSection("gallery")}
          >
            <i className="fas fa-images"></i>
            Gallery
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="Template_1-main-content">
        <div className="Template_1-content-container">
          <div className="Template_1-content-grid">
            {/* Left Column - Main Content */}
            <div className="Template_1-main-column">
              {/* Overview Section */}
              {activeSection === "overview" && (
                <div className="Template_1-content-section">
                  <h2 className="Template_1-section-title">
                    About This Journey
                  </h2>
                  <div className="Template_1-description">
                    {pkg.description && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: pkg.description.replace(/\r\n/g, "<br/>"),
                        }}
                      />
                    )}
                  </div>

                  {/* Highlights */}
                  {sightSeeings.length > 0 && (
                    <div className="Template_1-highlights">
                      <h3 className="Template_1-subtitle">
                        Experience Highlights
                      </h3>
                      <div className="Template_1-highlights-grid">
                        {sightSeeings.map((sight, i) => (
                          <div key={i} className="Template_1-highlight-card">
                            <div className="Template_1-highlight-icon">
                              <i className="fas fa-camera"></i>
                            </div>
                            <div className="Template_1-highlight-content">
                              <h4>{sight.sightseeingName}</h4>
                              <p>{sight.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Itinerary Section */}
              {activeSection === "itinerary" && (
                <div className="Template_1-content-section">
                  <h2 className="Template_1-section-title">
                    Your Journey Day by Day
                  </h2>
                  <div className="Template_1-itinerary">
                    {itineraries.map((day, i) => (
                      <div key={i} className="Template_1-itinerary-day">
                        <div className="Template_1-day-header">
                          <div className="Template_1-day-number">
                            Day {i + 1}
                          </div>
                          {/* <h3>{day.title}</h3> */}
                          <div className="Template_1-day-location">
                            <i className="fas fa-map-marker-alt"></i>
                            {typeof day.location === "object"
                              ? day.location.destinationName ||
                                "Unknown Location"
                              : day.location}
                          </div>
                        </div>
                        <div className="Template_1-day-content">
                          <p>{day.description}</p>
                          {day.meals && (
                            <div className="Template_1-day-meals">
                              <i className="fas fa-utensils"></i>
                              <strong>Meals Included:</strong> {day.meals}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Accommodation Section */}
              {activeSection === "accommodation" && (
                <div className="Template_1-content-section">
                  <h2 className="Template_1-section-title">
                    Your Accommodations
                  </h2>

                  {hotelBookings.length > 0 && (
                    <div className="Template_1-hotels">
                      {hotelBookings.map((hotel, i) => (
                        <div key={i} className="Template_1-hotel-card">
                          <div className="Template_1-hotel-image">
                            <div className="Template_1-hotel-placeholder">
                              <i className="fas fa-hotel"></i>
                            </div>
                          </div>
                          <div className="Template_1-hotel-details">
                            <h3>{hotel.hotelName}</h3>
                            <div className="Template_1-hotel-location">
                              <i className="fas fa-map-marker-alt"></i>
                              {typeof hotel.city === "object"
                                ? hotel.city.destinationName || "Unknown City"
                                : hotel.city}
                            </div>
                            <div className="Template_1-hotel-features">
                              <span className="Template_1-feature-tag">
                                <i className="fas fa-bed"></i> {hotel.roomType}
                              </span>
                              <span className="Template_1-feature-tag">
                                <i className="fas fa-moon"></i> {hotel.nights}{" "}
                                Nights
                              </span>
                            </div>
                            <div className="Template_1-hotel-rating">
                              {Array.from({ length: 5 }).map((_, idx) => (
                                <i
                                  key={idx}
                                  className={`fas fa-star ${
                                    idx < 4 ? "filled" : ""
                                  }`}
                                ></i>
                              ))}
                              <span className="Template_1-rating-text">
                                4.0/5
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {flightBookings.length > 0 && (
                    <div className="Template_1-flights">
                      <h3 className="Template_1-subtitle">Flight Details</h3>
                      {flightBookings.map((flight, i) => (
                        <div key={i} className="Template_1-flight-card">
                          <div className="Template_1-flight-header">
                            <div className="Template_1-airline">
                              <i className="fas fa-plane"></i>
                              {flight.airline}
                            </div>
                            <div className="Template_1-flight-number">
                              {flight.flightNumber}
                            </div>
                          </div>
                          <div className="Template_1-flight-route">
                            <div className="Template_1-departure">
                              <div className="Template_1-city">
                                {typeof flight.departureCity === "object"
                                  ? flight.departureCity.destinationName ||
                                    "Unknown City"
                                  : flight.departureCity}
                              </div>
                            </div>
                            <div className="Template_1-connection">
                              <div className="Template_1-connection-line"></div>
                              <i className="fas fa-long-arrow-alt-right"></i>
                            </div>
                            <div className="Template_1-arrival">
                              <div className="Template_1-city">
                                {typeof flight.arrivalCity === "object"
                                  ? flight.arrivalCity.destinationName ||
                                    "Unknown City"
                                  : flight.arrivalCity}
                              </div>
                            </div>
                          </div>
                          <div className="Template_1-flight-class">
                            <span className="Template_1-class-badge">
                              {flight.cabinClass}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {transfers.length > 0 && (
                    <div className="Template_1-transfers">
                      <h3 className="Template_1-subtitle">Transfers</h3>
                      <div className="Template_1-transfers-grid">
                        {transfers.map((transfer, i) => (
                          <div key={i} className="Template_1-transfer-card">
                            <div className="Template_1-transfer-icon">
                              <i className="fas fa-car"></i>
                            </div>
                            <div className="Template_1-transfer-details">
                              <h4>Transfer {i + 1}</h4>
                              <p>
                                <strong>From:</strong>{" "}
                                {typeof transfer.fromLocation === "object"
                                  ? transfer.fromLocation.destinationName ||
                                    "Unknown Location"
                                  : transfer.fromLocation}
                              </p>
                              <p>
                                <strong>To:</strong>{" "}
                                {typeof transfer.toLocation === "object"
                                  ? transfer.toLocation.destinationName ||
                                    "Unknown Location"
                                  : transfer.toLocation}
                              </p>
                              <p>
                                <strong>Vehicle:</strong>{" "}
                                {transfer.vehicleDetails}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Inclusions Section */}
              {activeSection === "inclusions" && (
                <div className="Template_1-content-section">
                  <h2 className="Template_1-section-title">What's Included</h2>

                  <div className="Template_1-inclusions-exclusions">
                    <div className="Template_1-inclusions">
                      <h3 className="Template_1-subtitle">
                        <i className="fas fa-check-circle"></i>
                        Included in your package
                      </h3>
                      <ul className="Template_1-inclusion-list">
                        {includes.map((item, idx) => (
                          <li key={idx}>
                            <i className="fas fa-check"></i>
                            {item.description}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="Template_1-exclusions">
                      <h3 className="Template_1-subtitle">
                        <i className="fas fa-times-circle"></i>
                        Not included in your package
                      </h3>
                      <ul className="Template_1-exclusion-list">
                        {excludes.map((item, idx) => (
                          <li key={idx}>
                            <i className="fas fa-times"></i>
                            {item.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {termsandConditions.length > 0 && (
                    <div className="Template_1-terms">
                      <h3 className="Template_1-subtitle">
                        Terms & Conditions
                      </h3>
                      <div className="Template_1-terms-list">
                        {termsandConditions.map((term, i) => (
                          <div key={i} className="Template_1-term-item">
                            <h4>{term.title}</h4>
                            <p>{term.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Gallery Section */}
              {activeSection === "gallery" && (
                <div className="Template_1-content-section">
                  <h2 className="Template_1-section-title">Package Gallery</h2>
                  <div className="Template_1-gallery-grid">
                    {imageGallery.map((img, index) => (
                      <div
                        key={index}
                        className="Template_1-gallery-item"
                        style={{ backgroundImage: `url(${img})` }}
                        onClick={() => setCurrentImageIndex(index)}
                      >
                        <div className="Template_1-gallery-overlay">
                          <i className="fas fa-search-plus"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Booking Widget */}
            <div className="Template_1-sidebar-column">
              <div className="Template_1-booking-widget">
                <div className="Template_1-widget-header">
                  <h3>Enquire About This Package</h3>
                  <div className="Template_1-widget-price">
                    <span className="Template_1-price-from">From</span>
                    <span className="Template_1-price-amount">
                      ₹{pkg.price?.toLocaleString("en-IN")}
                    </span>
                    <span className="Template_1-price-desc">per person</span>
                  </div>
                </div>

                <div className="Template_1-widget-details">
                  <div className="Template_1-detail-row">
                    <div className="Template_1-detail-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="Template_1-detail-content">
                      <span className="Template_1-detail-label">Duration</span>
                      <span className="Template_1-detail-value">
                        {pkg.duration}
                      </span>
                    </div>
                  </div>

                  <div className="Template_1-detail-row">
                    <div className="Template_1-detail-icon">
                      <i className="fas fa-users"></i>
                    </div>
                    <div className="Template_1-detail-content">
                      <span className="Template_1-detail-label">
                        Group Size
                      </span>
                      <span className="Template_1-detail-value">
                        {pkg.noOfPersons} People
                      </span>
                    </div>
                  </div>

                  <div className="Template_1-detail-row">
                    <div className="Template_1-detail-icon">
                      <i className="fas fa-user-tie"></i>
                    </div>
                    <div className="Template_1-detail-content">
                      <span className="Template_1-detail-label">Contact</span>
                      <span className="Template_1-detail-value">
                        {pkg.contactName}
                      </span>
                    </div>
                  </div>

                  <div className="Template_1-detail-row">
                    <div className="Template_1-detail-icon">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div className="Template_1-detail-content">
                      <span className="Template_1-detail-label">Phone</span>
                      <span className="Template_1-detail-value">
                        {pkg.contactPhoneNumber}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="Template_1-widget-actions">
                  <button
                    className="Template_1-primary-btn"
                    onClick={() => setShowEnquiryPopup(true)}
                  >
                    <i className="fas fa-envelope"></i>
                    Enquire Now
                  </button>
                </div>

                <div className="Template_1-widget-features">
                  <div className="Template_1-feature">
                    <i className="fas fa-shield-alt"></i>
                    <span>Free Cancellation</span>
                  </div>
                  <div className="Template_1-feature">
                    <i className="fas fa-credit-card"></i>
                    <span>No Booking Fees</span>
                  </div>
                  <div className="Template_1-feature">
                    <i className="fas fa-headset"></i>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEnquiryPopup && (
        <Template_1_EnquiryPopup
          packageDetails={pkg}
          onClose={() => setShowEnquiryPopup(false)}
        />
      )}
      {/* Fixed Footer */}

      <Template_1_Footer />
    </div>
  );
};

export default Template_1_PackageDetails;
